import React from 'react';

const ShippingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="bg-primary-600 px-6 py-4">
            <h1 className="text-3xl font-bold text-white">Shipping and Delivery Policy</h1>
            <p className="text-primary-100 mt-2">Last Updated: December 15, 2023</p>
          </div>
          
          <div className="px-6 py-8 prose prose-lg max-w-none">
            <h2>1. Overview</h2>
            <p>This Shipping and Delivery Policy outlines how Crawly Carl delivers our digital services to customers. Since we provide cloud-based API services and digital products, this policy covers service delivery, availability, and performance rather than physical shipping.</p>
            <p>This policy applies to all Crawly Carl services, including our web scraping API, dashboard access, Chrome extension, and any related digital services or features.</p>

            <h2>2. Digital Service Delivery</h2>
            
            <h3>2.1 Nature of Our Services</h3>
            <p>Crawly Carl provides exclusively digital services, including:</p>
            <ul>
              <li>Cloud-based web scraping API</li>
              <li>Web dashboard and account management interface</li>
              <li>Chrome browser extension</li>
              <li>API documentation and support resources</li>
              <li>Customer support services</li>
            </ul>
            
            <h3>2.2 Instant Digital Delivery</h3>
            <p>Our services are delivered digitally and instantaneously:</p>
            <ul>
              <li>API access is granted immediately upon account activation</li>
              <li>Dashboard access is available upon successful registration</li>
              <li>API keys are generated and delivered in real-time</li>
              <li>Service upgrades take effect immediately upon payment confirmation</li>
            </ul>

            <h2>3. Service Availability</h2>
            
            <h3>3.1 Uptime Commitment</h3>
            <p>We strive to maintain high service availability:</p>
            <ul>
              <li><strong>Target Uptime:</strong> 99.9% monthly availability</li>
              <li><strong>Service Monitoring:</strong> 24/7 automated monitoring</li>
              <li><strong>Status Updates:</strong> Real-time status at status.crawlycarl.com</li>
              <li><strong>Incident Response:</strong> Immediate response to service disruptions</li>
            </ul>
            
            <h3>3.2 Service Delivery Hours</h3>
            <p>Our API services are available 24/7, 365 days a year:</p>
            <ul>
              <li>API endpoints are accessible at all times</li>
              <li>Automated systems operate continuously</li>
              <li>Dashboard access is available around the clock</li>
              <li>Self-service features are always operational</li>
            </ul>

            <h2>4. API Service Delivery</h2>
            
            <h3>4.1 API Response Times</h3>
            <p>We maintain performance standards for API responses:</p>
            <ul>
              <li><strong>Simple Requests:</strong> Typically 1-5 seconds</li>
              <li><strong>Complex Scraping:</strong> Generally 5-30 seconds</li>
              <li><strong>Batch Operations:</strong> Processing time varies by size</li>
              <li><strong>Status Endpoints:</strong> Sub-second response times</li>
            </ul>
            
            <h3>4.2 Data Delivery Formats</h3>
            <p>API responses are delivered in standardized formats:</p>
            <ul>
              <li>JSON format for structured data</li>
              <li>Raw HTML when requested</li>
              <li>Error responses with detailed information</li>
              <li>Metadata including processing information</li>
            </ul>

            <h2>5. Account Activation</h2>
            
            <h3>5.1 Registration Process</h3>
            <p>Account activation is immediate upon completion of:</p>
            <ul>
              <li>Email address verification</li>
              <li>Account registration form submission</li>
              <li>Acceptance of terms and conditions</li>
              <li>Payment processing (for paid plans)</li>
            </ul>
            
            <h3>5.2 Access Credentials</h3>
            <p>Upon account activation, you receive immediate access to:</p>
            <ul>
              <li>Dashboard login credentials</li>
              <li>API authentication keys</li>
              <li>Documentation and getting started guides</li>
              <li>Support contact information</li>
            </ul>

            <h2>6. Service Performance Standards</h2>
            
            <h3>6.1 Performance Metrics</h3>
            <p>We monitor and maintain the following performance standards:</p>
            <ul>
              <li><strong>API Availability:</strong> 99.9% uptime target</li>
              <li><strong>Response Time:</strong> 95th percentile under 10 seconds</li>
              <li><strong>Success Rate:</strong> 99% successful API responses</li>
              <li><strong>Data Accuracy:</strong> High-quality extraction results</li>
            </ul>

            <h2>7. Geographic Availability</h2>
            
            <h3>7.1 Global Service Delivery</h3>
            <p>Our services are available globally with some considerations:</p>
            <ul>
              <li><strong>Primary Regions:</strong> North America, Europe, Asia-Pacific</li>
              <li><strong>CDN Distribution:</strong> Global content delivery network</li>
              <li><strong>Data Centers:</strong> Multiple geographic locations</li>
              <li><strong>Local Compliance:</strong> Adherence to regional regulations</li>
            </ul>

            <h2>8. Support Service Delivery</h2>
            
            <h3>8.1 Support Channels</h3>
            <p>Customer support is delivered through multiple channels:</p>
            <ul>
              <li><strong>Email Support:</strong> support@crawlycarl.com</li>
              <li><strong>Documentation:</strong> Comprehensive online guides</li>
              <li><strong>FAQ Section:</strong> Common questions and answers</li>
              <li><strong>Status Page:</strong> Real-time service status</li>
            </ul>
            
            <h3>8.2 Response Times</h3>
            <p>Support response time commitments:</p>
            <ul>
              <li><strong>General Inquiries:</strong> 24-48 hours</li>
              <li><strong>Technical Issues:</strong> 12-24 hours</li>
              <li><strong>Critical Issues:</strong> 2-6 hours</li>
              <li><strong>Emergency Outages:</strong> Immediate response</li>
            </ul>

            <h2>9. Contact Us</h2>
            <p>For questions about service delivery or this policy:</p>
            
            <div className="bg-gray-100 p-4 rounded-lg mt-4">
              <p><strong>Email:</strong> support@crawlycarl.com</p>
              <p><strong>Status Page:</strong> status.crawlycarl.com</p>
              <p><strong>Technical Support:</strong> tech@crawlycarl.com</p>
              <p><strong>Business Hours:</strong> Monday-Friday, 9 AM - 6 PM PST</p>
              <p><strong>Address:</strong> Crawly Carl<br />
              1234 Market Street<br />
              San Francisco, CA 94103</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPage;